// Shared SEO metadata map — imported by both the runtime SeoHead component
// (client) and the build-time prerender generator (scripts/generate-static-routes.js).
//
// Each entry maps a locale-stripped route subpath to the translation keys used to
// build that page's <title> and meta description. Titles/descriptions are derived
// from EXISTING translated hero strings so every locale gets unique, localized
// metadata for free — no new copy to translate.
//
// A route mapped to `null` (or absent) keeps the default shell title/description.
//
// The default shell title/description below double as the English home + fallback
// copy. They MUST match the literal strings in client/index.html verbatim (the
// prerender generator string-replaces them), so keep all three in sync.

export const BRAND = "Traverba";

// Note-taker-first positioning, aligned with the Play Store listing
// ("Traverba: AI Notes & Translate").
export const DEFAULT_TITLE =
  "Traverba — AI Notes & Translator | Transcribe, Summarize & Translate 100+ Languages";
export const DEFAULT_DESC =
  "Turn any voice into clean notes, an AI summary, and a chatbot you can ask anything — then translate across 100+ languages. Private, on-device AI — works offline. Free.";

// `headingKeys`/`introKey` (optional): keys for the PRERENDERED <h1> and intro
// paragraph, when a page's visible hero copy differs from its title tag. Default
// to `titleKeys`/`descKey`.
//
// `enTitle`/`enDesc` (optional): English-only literal override. Non-English
// locales fall back to the localized `titleKeys`/`descKey` hero strings. Home
// uses this so English keeps the keyword-rich branded title while every other
// locale gets its localized, note-taker-first hero copy.
export const seoRoutes = {
  "": {
    titleKeys: ["rb.landing.hero.headline"],
    descKey: "rb.landing.hero.sub",
    enTitle: DEFAULT_TITLE,
    enDesc: DEFAULT_DESC,
  },
  features: {
    titleKeys: ["featuresPage.title"],
    descKey: "featuresPage.subtitle",
    enTitle: "Features — Live Notes, Transcription, AI Summary & Translation | Traverba",
    enDesc:
      "Record and transcribe meetings, get AI summaries and ask-AI chat, and translate voice, camera and screens across 100+ languages — all on-device. See every Traverba feature.",
  },
  pricing: {
    titleKeys: ["pricing.title"],
    descKey: "pricing.subtitle",
    enTitle: "Pricing — Unlimited AI Notes & Translation | Traverba",
    enDesc:
      "One simple plan: unlimited transcription, AI summaries, ask-AI chat, and translation in 100+ languages. All features, no ads, no hidden limits. Free to start.",
  },
  about: {
    titleKeys: ["about.heroTitle1", "about.heroTitle2"],
    descKey: "about.heroSubtitle",
    enTitle: "About Traverba — Private AI Notes & Translator, Built in Hong Kong",
    enDesc:
      "Traverba builds a private, on-device AI note taker and translator — transcribe, summarize and translate 100+ languages. Making conversation accessible and private for everyone.",
  },
  contact: {
    titleKeys: ["contact.title"],
    descKey: "contact.subtitle",
    enTitle: "Contact & Support | Traverba",
    enDesc:
      "Questions, bugs or feature requests for Traverba's AI note taker and translator? Get in touch — we'd love to hear from you.",
  },
  download: {
    titleKeys: ["download.title"],
    descKey: "download.subtitle",
    enTitle: "Download Traverba — Free AI Notes & Translator for iOS & Android",
    enDesc:
      "Get Traverba free on iOS and Android. Record, transcribe and summarize meetings with AI, then translate across 100+ languages — on-device and offline.",
  },
  enterprise: {
    titleKeys: ["enterprise.hero.title1", "enterprise.hero.title2"],
    descKey: "enterprise.hero.description",
    enTitle: "Enterprise — Real-Time Transcription & Translation for Events | Traverba",
    enDesc:
      "AI-powered real-time transcription and live captioning for conferences and global events. Break language barriers with instant subtitles in 100+ languages.",
  },
  // Blog listing (/blog). Blog POST detail pages manage their own localized meta
  // in BlogPost.tsx + the prerender generator, so they are intentionally NOT here.
  blog: {
    titleKeys: ["nav.blog"],
    descKey: "blog.heroSubtitle",
    enTitle: "Traverba Blog — On-Device AI, Translation & AI Meeting Notes",
    enDesc:
      "Guides on on-device AI, translation technology, AI meeting notes, travel tips, and building a privacy-first translator app from Hong Kong.",
  },
  // Pillar pages — hero copy lives under rb.{notes|translator|agent}.hero.*
  "live-notes": {
    titleKeys: ["rb.notes.hero.headline"],
    descKey: "rb.notes.hero.sub",
    enTitle: "Live Notes — AI Meeting Recorder, Transcription & Summary | Traverba",
    enDesc:
      "Record or import a meeting and Traverba transcribes it, then hands you an AI summary, decisions and action items — all on-device. Ask-AI chat over any transcript.",
  },
  "live-translator": {
    titleKeys: ["rb.translator.hero.headline"],
    descKey: "rb.translator.hero.sub",
    // Emits FAQPage JSON-LD and folds the Q&A into the prerendered body.
    faqKey: "rb.translator.faq",
    // Prose explainer, also folded into the prerendered body.
    deepDiveKey: "rb.translator.deepDive",
    enTitle: "Live Translator — Real-Time Voice, Camera & Screen Translation | Traverba",
    enDesc:
      "Real-time voice, camera and screen translation in 100+ languages — face to face or on the go. On-device by default, works offline, with optional cloud AI.",
  },
  "live-agent": {
    titleKeys: ["rb.agent.hero.headline"],
    descKey: "rb.agent.hero.sub",
    enTitle: "Live Cue — Your AI Partner for Every Conversation | Traverba",
    enDesc:
      "An early concept from Traverba: an AI assistant that understands what's being said, reads the room, and suggests your next move in any conversation.",
  },
  // BYO-LLM guides. These pages set their own <head> at runtime, which the
  // prerenderer never executes — so without an entry here the crawled HTML
  // carried DEFAULT_TITLE and no body at all. `metaTitle`/`metaDescription`
  // already exist in all 20 locales; `headingKeys`/`introKey` keep the
  // prerendered <h1>/intro matching the page's real visible hero copy rather
  // than repeating the (deliberately query-shaped) title tag.
  "groq-api-key": {
    titleKeys: ["groqApiKey.metaTitle"],
    descKey: "groqApiKey.metaDescription",
    headingKeys: ["groqApiKey.titleA", "groqApiKey.titleB"],
    introKey: "groqApiKey.subtitle",
  },
  "gemini-api-key": {
    titleKeys: ["geminiApiKey.metaTitle"],
    descKey: "geminiApiKey.metaDescription",
    headingKeys: ["geminiApiKey.titleA", "geminiApiKey.titleB"],
    introKey: "geminiApiKey.subtitle",
  },
  "byo-llm-setup": {
    titleKeys: ["byoLlm.metaTitle"],
    descKey: "byoLlm.metaDescription",
    headingKeys: ["byoLlm.titleA", "byoLlm.titleB"],
    introKey: "byoLlm.subtitle",
  },
};

// Locale dir slug -> Open Graph locale (facebook-style, language_TERRITORY)
export const ogLocaleFor = {
  en: "en_US",
  "zh-hant": "zh_TW",
  "zh-hans": "zh_CN",
  ja: "ja_JP",
  ko: "ko_KR",
  th: "th_TH",
  vi: "vi_VN",
  id: "id_ID",
  es: "es_ES",
  fr: "fr_FR",
  de: "de_DE",
  pt: "pt_BR",
  it: "it_IT",
  ru: "ru_RU",
  tr: "tr_TR",
  nl: "nl_NL",
  ar: "ar_AR",
  hi: "hi_IN",
  ms: "ms_MY",
  pl: "pl_PL",
};

// Build a branded title: "Page Heading | Traverba". Empty parts are dropped.
export function buildTitle(parts) {
  const heading = (parts || []).filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  if (!heading) return "";
  // Avoid doubling the brand if a heading already contains it.
  if (heading.toLowerCase().includes(BRAND.toLowerCase())) return heading;
  return `${heading} | ${BRAND}`;
}

/**
 * Resolve a route's { title, description } for a locale. Shared by the runtime
 * SeoHead component and the build-time generator so both stay in lockstep.
 *
 * @param entry      a seoRoutes[route] object (or undefined)
 * @param slug       locale slug (e.g. "en", "ja")
 * @param resolveKey (key) => string | null — resolves an i18n key for this locale
 */
export function resolveMeta(entry, slug, resolveKey) {
  if (!entry) return {};
  let title;
  if (slug === "en" && entry.enTitle) title = entry.enTitle;
  else title = buildTitle((entry.titleKeys || []).map(resolveKey).filter(Boolean));

  let description;
  if (slug === "en" && entry.enDesc) description = entry.enDesc;
  else description = resolveKey(entry.descKey) || undefined;

  return { title: title || undefined, description: description || undefined };
}
